// src/components/Layout/Layout.tsx

import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconMail, IconLocation } from "../icon/Icon";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { useTitle } from "../../contexts/TitleContext/TitleContext";
import "./Layout.scss";

interface ContactCardProps {
    icon: React.ReactNode;
    title: string;
    content: string;
    href?: string;
}

const ContactCard: React.FC<ContactCardProps> = ({ icon, title, content, href }) => (
    <div className="contact-card">
        <div className="icon-wrapper">{icon}</div>
        <div className="content">
            <h3 className="contact-title">{title}</h3>
            {href ? (
                <a href={href} className="contact-link">
                    {content}
                </a>
            ) : (
                <p className="contact-text">{content}</p>
            )}
        </div>
    </div>
);

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [isScrolled, setIsScrolled] = useState(false);
    const { t, i18n } = useTranslation();
    const { setTitle } = useTitle();

    // Debounced scroll handler
    const handleScroll = useCallback(() => {
        let timeoutId: NodeJS.Timeout;

        return () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setIsScrolled(window.scrollY > 0);
            }, 10);
        };
    }, []);

    // Update the document title when language changes
    useEffect(() => {
        // Get the current page title from location
        let pageTitle = "";
        if (location.pathname === "/") {
            pageTitle = t("navigation.home");
        } else if (location.pathname === "/turnieje") {
            pageTitle = t("navigation.tournaments");
        } else if (location.pathname === "/dodaj") {
            pageTitle = t("navigation.addTournament");
        } else if (location.pathname === "/import") {
            pageTitle = t("navigation.import");
        }

        const appName = t("general.appName", "Turnieje Darta");
        if (pageTitle) {
            setTitle(`${pageTitle} | ${appName}`);
        } else {
            setTitle(appName);
        }
    }, [location.pathname, t, i18n.language, setTitle]);

    useEffect(() => {
        const scrollHandler = handleScroll();
        window.addEventListener("scroll", scrollHandler);

        // Initial check
        setIsScrolled(window.scrollY > 0);

        // Cleanup
        return () => {
            window.removeEventListener("scroll", scrollHandler);
        };
    }, [handleScroll]);

    // Get the app name from translations
    const appName = t("general.appName", "Turnieje Darta");

    return (
        <>
            <header className={`layout-header ${isScrolled ? "scrolled" : ""}`}>
                <div className="header-content">
                    <div className="logo">
                        <Link to="/">{appName}</Link>
                    </div>
                    <nav className="main-nav">
                        <Link to="/turnieje" className={location.pathname === "/turnieje" ? "active" : ""}>
                            {t("navigation.tournaments")}
                        </Link>
                        <Link
                            to="/dodaj"
                            className={`add-tournament ${location.pathname === "/dodaj" ? "active" : ""}`}
                        >
                            {t("navigation.addTournament")}
                        </Link>
                        <LanguageSwitcher />
                    </nav>
                </div>
            </header>
            <main className="layout-main">{children}</main>
            <footer className="layout-footer">
                {/* Contact Section */}
                <section className="contact-section">
                    <div className="section-header">
                        <h2 className="section-title">{t("footer.contactUs")}</h2>
                    </div>
                    <div className="contact-cards">
                        <ContactCard
                            icon={<IconMail />}
                            title={t("footer.email")}
                            content="turnieje@ligadarta.pl"
                            href="mailto:turnieje@ligadarta.pl"
                        />
                        <ContactCard
                            icon={<IconLocation />}
                            title={t("footer.messenger")}
                            content="Mariusz"
                            href="http://m.me/mariusz.swiatkowski/"
                        />
                    </div>
                </section>
                <section className="legal">
                    <span>
                        {t("footer.copyright")} <em>"Święty"</em>
                    </span>
                    <span>{t("footer.letsPlay")}</span>
                </section>
            </footer>
        </>
    );
};

export default Layout;
